import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Analysis, CoverLetter, InterviewPrep, Communications, TailoredResume } from '@/components/ai-tools';
import { Saved } from '@/components/workspace/tailoring/Saved';
import { Library, Briefcase, FileSignature, Users, Send, FileText, FileJson, Search } from 'lucide-react';
import { useWorkspaceContext } from '@/components/workspace/WorkspaceContext';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { TabLoader } from './TabLoader';
import { MainEmpty } from './MainEmpty';
import { MainError } from './MainError';

export const Main: React.FC = () => {
  const {
    rightView, setRightView, status, statusMessage, setStatus,
    reviewResult, coverLetterResult, interviewPrepResult, communicationsResult, tailoredResumeResult,
    setTailoredResumeResult, setCommunicationsResult, activeAnalysisTab,
    handleSaveOutput, isSaving, resumes, selectedId, jobText,
    outputs, openedOutputId, handleOpenOutput, deleteOutput, handleUpdateStatus,
    isGenerating,
    handleAnalyze, handleGenerateCoverLetter, handleGenerateTailoredResume,
    handleGenerateInterviewPrep, handleGenerateCommunications, cancelGeneration
  } = useWorkspaceContext();

  const { user } = useAuth();

  const requireAuth = (action: (...args: any[]) => void) => {
    return (...args: any[]) => {
      if (!user) {
        window.dispatchEvent(new CustomEvent('app:require-login'));
        return;
      }
      if (!user.emailVerified) {
        toast.error('Please verify your email address to continue.');
        return;
      }
      action(...args);
    };
  };

  return (
    <div id="results-area" className="flex-1 bg-slate-50/50 dark:bg-slate-950 flex flex-col z-0 lg:h-full min-h-0 lg:overflow-hidden relative">
      
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(255,255,255,0.03)_0%,_transparent_50%)] pointer-events-none z-0"></div>

      <div className="h-12 lg:h-14 border-b border-slate-300 dark:border-slate-800 bg-slate-50 dark:bg-[#0A0D15] shrink-0 flex items-center justify-between px-2 lg:px-4 z-50 sticky top-14 lg:top-0 dark:shadow-sm">
        <div className="flex items-center gap-1 lg:gap-2 h-full overflow-x-auto overflow-y-hidden scrollbar-hide">
          <button
            onClick={() => setRightView('history')}
            className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 h-full text-xs lg:text-sm font-medium border-b-2 whitespace-nowrap cursor-pointer ${rightView === 'history' || rightView === 'empty' ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
          >
            <Library className="w-3.5 h-3.5 lg:w-4 lg:h-4" /> History
          </button>
          {(reviewResult || isGenerating?.analysis) && (
            <button
              onClick={() => setRightView('analysis')}
              className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 h-full text-xs lg:text-sm font-medium border-b-2 whitespace-nowrap cursor-pointer ${rightView === 'analysis' ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
            >
               <Briefcase className="w-3.5 h-3.5 lg:w-4 lg:h-4" /> Analysis
            </button>
          )}
          {(coverLetterResult || isGenerating?.coverLetter) && (
            <button
              onClick={() => setRightView('coverLetter')}
              className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 h-full text-xs lg:text-sm font-medium border-b-2 whitespace-nowrap cursor-pointer ${rightView === 'coverLetter' ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
            >
              <FileSignature className="w-3.5 h-3.5 lg:w-4 lg:h-4" /> Cover Letter
            </button>
          )}
          {(tailoredResumeResult || isGenerating?.tailoredResume) && (
            <button
              onClick={() => setRightView('tailoredResume')}
              className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 h-full text-xs lg:text-sm font-medium border-b-2 whitespace-nowrap cursor-pointer ${rightView === 'tailoredResume' ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
            >
              <FileJson className="w-3.5 h-3.5 lg:w-4 lg:h-4" /> Tailored
            </button>
          )}
          {(interviewPrepResult || isGenerating?.interviewPrep) && (
            <button
              onClick={() => setRightView('interviewPrep')}
              className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 h-full text-xs lg:text-sm font-medium border-b-2 whitespace-nowrap cursor-pointer ${rightView === 'interviewPrep' ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
            >
              <Users className="w-3.5 h-3.5 lg:w-4 lg:h-4" /> Interview Prep
              <span className="ml-0.5 text-[9px] bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 px-1.5 py-0.5 rounded-sm font-bold uppercase">Beta</span>
            </button>
          )}
          {(communicationsResult || isGenerating?.communications) && (
            <button
              onClick={() => setRightView('communications')}
              className={`flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 h-full text-xs lg:text-sm font-medium border-b-2 whitespace-nowrap cursor-pointer ${rightView === 'communications' ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'}`}
            >
              <Send className="w-3.5 h-3.5 lg:w-4 lg:h-4" /> Outreach
            </button>
          )}
        </div>
        
        {/* Global Save Button */}
        {(reviewResult || coverLetterResult || tailoredResumeResult || interviewPrepResult || communicationsResult) && rightView !== 'empty' && rightView !== 'history' && (
          <button
            onClick={handleSaveOutput}
            disabled={isSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors cursor-pointer disabled:cursor-not-allowed shrink-0 ml-4 dark:shadow-sm"
          >
            {isSaving ? (
              <svg className="animate-spin w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
            )}
            <span className="hidden sm:inline">{isSaving ? 'Saving...' : (openedOutputId ? 'Update Application' : 'Save Application')}</span>
          </button>
        )}
      </div>
      
      <div className="flex-1 p-4 md:px-8 md:py-4 lg:px-10 lg:py-4 xl:p-12 relative z-10 flex flex-col lg:overflow-hidden">
        <AnimatePresence mode="wait">
          {status === 'error' ? (
            <MainError statusMessage={statusMessage} onDismiss={() => setStatus('idle')} />
          ) : rightView === 'empty' || rightView === 'history' ? (
            <motion.div 
              key="history" initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.15 }}
              className="max-w-5xl w-full mx-auto h-full flex flex-col"
            >
              {(!reviewResult && !coverLetterResult && outputs.length === 0) ? (
                <MainEmpty />
              ) : (
                <div className="flex-1 flex flex-col px-4 lg:px-8 py-4 lg:py-8 overflow-y-auto overflow-x-hidden">
                  <Saved outputs={outputs} onOpen={handleOpenOutput} onDelete={deleteOutput} onUpdateOutput={handleUpdateStatus} />
                </div>
              )}
            </motion.div>
          ) : rightView === 'analysis' ? (
            isGenerating?.analysis ? (
              <TabLoader key="analysis-loader" message="Analyzing resume..." icon={<Briefcase className="w-6 h-6 text-slate-700 dark:text-slate-400" />} onCancel={cancelGeneration} />
            ) : reviewResult ? (
              <motion.div
                key="analysis" initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.15 }}
                className="max-w-5xl w-full mx-auto h-full flex flex-col"
              >
                <Analysis 
                  result={reviewResult} 
                  activeTab={activeAnalysisTab} 
                  onSave={handleSaveOutput} 
                  isSaving={isSaving}
                  resume={resumes.find(r => r.id === selectedId)}
                  jobText={jobText}
                  onRetry={requireAuth(handleAnalyze)}
                />
              </motion.div>
            ) : null
          ) : rightView === 'coverLetter' ? (
            isGenerating?.coverLetter ? (
              <TabLoader key="coverletter-loader" message="Generating cover letter..." icon={<FileSignature className="w-6 h-6 text-slate-700 dark:text-slate-400" />} onCancel={cancelGeneration} />
            ) : coverLetterResult ? (
              <motion.div
                key="coverLetter" initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.15 }}
                className="max-w-5xl w-full mx-auto h-full flex flex-col"
              >
                <CoverLetter result={coverLetterResult} onSave={handleSaveOutput} isSaving={isSaving} onRetry={requireAuth(handleGenerateCoverLetter)} />
              </motion.div>
            ) : null
          ) : rightView === 'interviewPrep' ? (
            isGenerating?.interviewPrep ? (
              <TabLoader key="interview-loader" message="Generating guide..." icon={<Users className="w-6 h-6 text-slate-700 dark:text-slate-400" />} onCancel={cancelGeneration} />
            ) : interviewPrepResult ? (
              <motion.div
                key="interviewPrep" initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.15 }}
                className="max-w-5xl w-full mx-auto h-full flex flex-col"
              >
                <InterviewPrep result={interviewPrepResult} onSave={handleSaveOutput} isSaving={isSaving} onRetry={requireAuth(handleGenerateInterviewPrep)} />
              </motion.div>
            ) : null
          ) : rightView === 'communications' ? (
            communicationsResult || isGenerating?.communications ? (
              <motion.div
                key="communications" initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.15 }}
                className="max-w-5xl w-full mx-auto h-full flex flex-col"
              >
                <Communications 
                  result={communicationsResult!} 
                  onSave={handleSaveOutput} 
                  isSaving={isSaving} 
                  isGenerating={!!isGenerating?.communications}
                  onChange={setCommunicationsResult || (() => {})} 
                  onRegenerate={requireAuth(handleGenerateCommunications)} 
                  onCancel={cancelGeneration}
                />
              </motion.div>
            ) : null
          ) : rightView === 'tailoredResume' && isGenerating?.tailoredResume ? (
            <TabLoader key="tailoredresume-loader" message="Tailoring resume..." icon={<FileJson className="w-6 h-6 text-slate-700 dark:text-slate-400" />} onCancel={cancelGeneration} />
          ) : null}
        </AnimatePresence>

        <div 
          style={{ 
            display: rightView === 'tailoredResume' && tailoredResumeResult && !isGenerating?.tailoredResume ? 'block' : 'none',
            opacity: rightView === 'tailoredResume' ? 1 : 0,
            transform: `translateY(${rightView === 'tailoredResume' ? '0' : '5px'})`,
            transition: 'opacity 0.15s, transform 0.15s'
          }}
          className="max-w-5xl mx-auto h-full flex flex-col w-full"
        >
          {tailoredResumeResult && (
            <TailoredResume 
              result={tailoredResumeResult} 
              onSave={handleSaveOutput} 
              isSaving={isSaving} 
              resume={resumes.find(r => r.id === selectedId)}
              jobText={jobText}
              recommendations={reviewResult?.review?.sectionRecommendations}
              onUpdateResult={setTailoredResumeResult}
              onRetry={() => requireAuth(handleGenerateTailoredResume)()}
            />
          )}
        </div>
      </div>
    </div>
  );
};
